import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useElementWidth } from '../../../../../../utils/customHooks';

const widthOf = (element: Element) => element.getBoundingClientRect().width;

type MetricFit = { count: number; overflow: boolean };

/** Leading metrics that fit with the overflow marker; the first metric always shows, alone when the marker cannot fit beside it. */
const fitMetrics = (metricWidths: number[], overflowWidth: number, availableWidth: number): MetricFit => {
    const totalWidth = metricWidths.reduce((sum, width) => sum + width, 0);
    if (totalWidth <= availableWidth) return { count: metricWidths.length, overflow: false };
    let usedWidth = overflowWidth;
    let count = 0;
    while (count < metricWidths.length && usedWidth + metricWidths[count] <= availableWidth) {
        usedWidth += metricWidths[count];
        count += 1;
    }
    return count ? { count, overflow: true } : { count: 1, overflow: false };
};

/** Counts the metrics that fit the frame, measured off a hidden full-length copy so the count never feeds back into its own input. */
export const useFittedMetricCount = (metricCount: number, contentKey: string) => {
    const frameElementRef = useRef<HTMLDivElement | null>(null);
    const measureElementRef = useRef<HTMLSpanElement | null>(null);
    const [observeFrame, frameWidth] = useElementWidth();
    const [observeMeasure, measureWidth] = useElementWidth();
    const [fit, setFit] = useState<MetricFit>({ count: metricCount, overflow: false });

    const frameRef = useCallback(
        (element: HTMLDivElement | null) => {
            frameElementRef.current = element;
            observeFrame(element);
        },
        [observeFrame],
    );
    const measureRef = useCallback(
        (element: HTMLSpanElement | null) => {
            measureElementRef.current = element;
            observeMeasure(element);
        },
        [observeMeasure],
    );

    useLayoutEffect(() => {
        const frame = frameElementRef.current;
        const measure = measureElementRef.current;
        if (!frame || !measure) return;
        const availableWidth = widthOf(frame);
        if (!availableWidth) {
            setFit({ count: metricCount, overflow: false });
            return;
        }
        const metricWidths = Array.from(measure.children, widthOf);
        const overflowWidth = metricWidths.pop() ?? 0;
        setFit(fitMetrics(metricWidths, overflowWidth, availableWidth));
    }, [frameWidth, measureWidth, metricCount, contentKey]);

    return { frameRef, measureRef, fit };
};
